import React, { FC, useState } from 'react';
import { makeStyles, Theme, createStyles, Button, Menu, MenuItem, Typography, FormControl, Select } from '@material-ui/core';
import SettingsIcon from '@material-ui/icons/Settings';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import { useLicenseEndpoint, LicenseEndpointOption } from '../../../context/licenseEndpointContext';
import { getAvailableEndpoints } from '../../../utils/licenseEndpointConfig';

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		buttonLabel: {
			[theme.breakpoints.down('sm')]: {
				display: 'none'
			}
		},
		inlineContainer: {
			display: 'inline-flex',
			alignItems: 'center',
			gap: theme.spacing(1),
			margin: theme.spacing(1),
		},
		label: {
			fontSize: '0.875rem',
			fontWeight: 500
		},
		select: {
			fontSize: '0.875rem'
		}
	})
);

export interface LicenseEndpointSelectorConfig {
	/** Unique identifier for this selector instance */
	instanceId: string;
	/** Label to display */
	label?: string;
	variant?: 'button' | 'inline';
	className?: string;
}

export const ConfigurableLicenseEndpointSelector: FC<LicenseEndpointSelectorConfig> = ({
	instanceId,
	label,
	variant = 'inline',
	className
}) => {
	const classes = useStyles();
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);
	const { selectedEndpoint, setSelectedEndpoint } = useLicenseEndpoint(instanceId);

	const availableEndpoints = getAvailableEndpoints();
	
	if (!availableEndpoints.hasMultiple) {
		return null;
	}

	const { options, defaultOption } = availableEndpoints;
	const currentSelection = options.includes(selectedEndpoint) ? selectedEndpoint : defaultOption;

	const endpointLabels: Record<LicenseEndpointOption, string> = {
		auto: 'Auto',
		arcus: 'Arcus (LAG)',
		playfactory: 'PlayFactory'
	};

	const handleMenuClick = (event: React.MouseEvent<HTMLElement>): void => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = (): void => {
		setAnchorEl(null);
	};

	const handleSelection = (endpoint: LicenseEndpointOption): void => {
		handleClose();
		setSelectedEndpoint(endpoint);
	};

	const handleSelectChange = (event: React.ChangeEvent<{ value: unknown }>): void => {
		setSelectedEndpoint(event.target.value as LicenseEndpointOption);
	};

	if (variant === 'button') {
		return (
			<div className={className}>
				<Button
					data-testid={`license-endpoint-selector-${instanceId}`}
					aria-label='License Endpoint Selector'
					variant='text'
					color='secondary'
					size='small'
					startIcon={<SettingsIcon />}
					endIcon={<KeyboardArrowDownIcon />}
					onClick={handleMenuClick}
					style={{ justifyContent: 'flex-start', padding: 0, textTransform: 'none' }}>
					<Typography variant='body2'>
					{label ? `${label}: ${endpointLabels[currentSelection]}` : endpointLabels[currentSelection]}
					</Typography>
				</Button>
				<Menu
					data-testid={`license-endpoint-menu-${instanceId}`}
					anchorEl={anchorEl}
					getContentAnchorEl={null}
					anchorOrigin={{
						vertical: 'bottom',
						horizontal: 'center'
					}}
					keepMounted
					transformOrigin={{
						vertical: 'top',
						horizontal: 'center'
					}}
					open={open}
					onClose={handleClose}>
					{options.map((endpoint) => (
						<MenuItem
							key={endpoint}
							data-testid={`license-endpoint-menu-${instanceId}-${endpoint}`}
							onClick={(): void => handleSelection(endpoint)}>
							<Typography variant='button'>{endpointLabels[endpoint]}</Typography>
						</MenuItem>
					))}
				</Menu>
			</div>
		);
	}

	return (
		<div className={classes.inlineContainer}>
			{label && <Typography className={classes.label}>{label}</Typography>}
			<FormControl variant='outlined' size='small'>
				<Select
					value={currentSelection}
					onChange={handleSelectChange}
					className={classes.select}
					data-testid={`license-endpoint-selector-${instanceId}`}>
					{options.map((endpoint) => (
						<MenuItem key={endpoint} value={endpoint}>
							{endpointLabels[endpoint]}
						</MenuItem>
					))}
				</Select>
			</FormControl>
		</div>
	);
};
